
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';

interface RatingStarsProps {
  rating: number;
  maxRating?: number;
  size?: 'sm' | 'md' | 'lg';
  interactive?: boolean;
  onRatingChange?: (rating: number) => void;
  className?: string; // Added className prop
}

export const RatingStars = ({
  rating,
  maxRating = 5,
  size = 'md',
  interactive = false,
  onRatingChange,
  className,
}: RatingStarsProps) => {
  const sizeClasses = {
    sm: 'w-3.5 h-3.5',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  const handleClick = (index: number) => {
    if (interactive && onRatingChange) {
      onRatingChange(index + 1);
    }
  };

  // Calculate the full stars and partial stars
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.3 && rating % 1 <= 0.7;
  const emptyStars = maxRating - fullStars - (hasHalfStar ? 1 : 0);

  return (
    <div className={cn("flex items-center gap-0.5", className)}>
      {/* Full stars */}
      {Array.from({ length: fullStars }).map((_, index) => (
        <Star
          key={`full-${index}`}
          className={cn(
            sizeClasses[size],
            'text-amber-400 fill-amber-400',
            interactive && 'cursor-pointer hover:text-amber-500'
          )}
          onClick={() => handleClick(index)}
        />
      ))}
      
      {/* Half star */}
      {hasHalfStar && (
        <div className="relative">
          <Star
            className={cn(
              sizeClasses[size],
              'text-amber-400',
              interactive && 'cursor-pointer hover:text-amber-500'
            )}
          />
          <div className="absolute top-0 left-0 w-1/2 overflow-hidden">
            <Star
              className={cn(
                sizeClasses[size],
                'text-amber-400 fill-amber-400',
                interactive && 'cursor-pointer hover:text-amber-500'
              )}
            />
          </div>
        </div>
      )}
      
      {/* Empty stars */}
      {Array.from({ length: emptyStars }).map((_, index) => (
        <Star
          key={`empty-${index}`}
          className={cn(
            sizeClasses[size],
            'text-gray-300',
            interactive && 'cursor-pointer hover:text-amber-400'
          )}
          onClick={() => handleClick(fullStars + (hasHalfStar ? 1 : 0) + index)}
        />
      ))}
    </div>
  );
};
